import React, { useState } from 'react';
import { useData } from '../../store/DataContext';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Search, Plus, Eye, Download, Printer } from 'lucide-react';
import { BillingSection } from './BillingModule';
import { formatCurrency } from './utils';
interface Props {
  onNavigate: (section: BillingSection) => void;
}
export const Quotations: React.FC<Props> = ({
  onNavigate
}) => {
  const {
    quotations,
    patients,
    walkInClients
  } = useData();
  const [searchQuery, setSearchQuery] = useState('');
  const filteredQuotations = quotations.filter(q => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    if (q.quotationNumber.toLowerCase().includes(query)) return true;
    let customerName = '';
    if (q.customerType === 'Patient') {
      const p = patients.find(p => p.id === q.customerId);
      if (p) customerName = `${p.firstName} ${p.lastName}`;
    } else {
      const c = walkInClients.find(c => c.id === q.customerId);
      if (c) customerName = c.name;
    }
    return customerName.toLowerCase().includes(query);
  });
  const getCustomerName = (customerId: string, type: 'Patient' | 'WalkIn') => {
    if (type === 'Patient') {
      const p = patients.find(p => p.id === customerId);
      return p ? `${p.firstName} ${p.lastName}` : 'Unknown Patient';
    } else {
      const c = walkInClients.find(c => c.id === customerId);
      return c ? c.name : 'Unknown Client';
    }
  };
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Accepted':
        return 'bg-green-100 text-green-800';
      case 'Declined':
        return 'bg-red-100 text-red-800';
      case 'Sent':
        return 'bg-blue-100 text-blue-800';
      case 'Invoiced':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  return <div className="space-y-6" data-id="element-1456">
      <div className="flex justify-between items-center" data-id="element-1457">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-1458">Quotations</h1>
        <Button onClick={() => onNavigate('new-quotation' as any)} data-id="element-1459">
          <Plus className="w-4 h-4 mr-2" data-id="element-1460" /> New Quotation
        </Button>
      </div>

      <Card noPadding data-id="element-1461">
        <div className="p-4 border-b border-gray-200 bg-gray-50" data-id="element-1462">
          <div className="relative max-w-md" data-id="element-1463">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1464">
              <Search className="h-5 w-5 text-gray-400" data-id="element-1465" />
            </div>
            <input type="text" className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm" placeholder="Search by quotation number or customer name..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-1466" />
          </div>
        </div>

        <div className="overflow-x-auto" data-id="element-1467">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-1468">
            <thead className="bg-gray-50" data-id="element-1469">
              <tr data-id="element-1470">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1471">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1472">
                  Quotation#
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1473">
                  Customer
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1474">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1475">
                  Amount
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1476">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-1477">
              {filteredQuotations.length > 0 ? filteredQuotations.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).map(quo => <tr key={quo.id} className="hover:bg-gray-50" data-id="element-1478">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1479">
                        {new Date(quo.date).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-600" data-id="element-1480">
                        {quo.quotationNumber}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-1481">
                        {getCustomerName(quo.customerId, quo.customerType)}
                        <span className="ml-2 text-xs text-gray-400 font-normal" data-id="element-1482">
                          ({quo.customerType})
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center" data-id="element-1483">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(quo.status)}`} data-id="element-1484">
                          {quo.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-medium text-gray-900" data-id="element-1485">
                        {formatCurrency(quo.totalAmount)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-1486">
                        <button className="text-gray-400 hover:text-blue-600 mx-1" title="View" data-id="element-1487">
                          <Eye className="w-4 h-4" data-id="element-1488" />
                        </button>
                        <button className="text-gray-400 hover:text-gray-600 mx-1" title="Print" data-id="element-1489">
                          <Printer className="w-4 h-4" data-id="element-1490" />
                        </button>
                        <button className="text-gray-400 hover:text-gray-600 mx-1" title="Download" data-id="element-1491">
                          <Download className="w-4 h-4" data-id="element-1492" />
                        </button>
                      </td>
                    </tr>) : <tr data-id="element-1493">
                  <td colSpan={6} className="px-6 py-12 text-center text-gray-500" data-id="element-1494">
                    No quotations found.
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>
      </Card>
    </div>;
};